import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  Paper,
  Typography,
  Button,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  IconButton,
  TextField,
  Chip,
  CircularProgress,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useApi } from "../../utils/api";
import { useAuth } from "../../utils/auth";
import { useUserInfo } from "../../utils/user";

const PLATFORM_OWNER_USER_ID = import.meta.env.VITE_PLATFORM_OWNER_USER_ID;
import AddClient from "./AddClient";
import Loading from "../Layout/Loading";
import ClientWorkoutCalendar from "./Client";
import ManageProductsDialog from "./ManageProductsDialog";
import CoachSubscriptionModal from "./CoachSubscriptionModal";
import CoachUpgradeModal from "./CoachUpgradeModal";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const appearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "#ff0000",
    fontFamily: "Roboto Mono, monospace",
  },
};

function getCurrentWeekUnixRange() {
  const start = moment().startOf("week");
  const end = moment().endOf("week").endOf("day");
  return {
    startUnix: start.unix(),
    endUnix: end.unix(),
    weekLabel: `Week of ${start.format("MMM D")}`,
  };
}

/** In-memory cache so leaving /clients and returning does not remount-fetch everything. */
let clientsRouteCache = {
  storageKey: null,
  clients: [],
  coachProfile: null,
  weekSummaries: {},
  activeClientId: null,
};

function clientsRouteCacheKey(coachId) {
  if (!coachId) return null;
  const { weekLabel } = getCurrentWeekUnixRange();
  return `${coachId}::${weekLabel}`;
}

function readClientsRouteCache(coachId) {
  const k = clientsRouteCacheKey(coachId);
  if (!k || clientsRouteCache.storageKey !== k) return null;
  return {
    clients: clientsRouteCache.clients,
    coachProfile: clientsRouteCache.coachProfile,
    weekSummaries: clientsRouteCache.weekSummaries,
    activeClientId: clientsRouteCache.activeClientId,
  };
}

function writeClientsRouteCache(coachId, patch) {
  const k = clientsRouteCacheKey(coachId);
  if (!k) return;
  clientsRouteCache = {
    storageKey: k,
    clients: patch.clients ?? clientsRouteCache.clients,
    coachProfile:
      patch.coachProfile !== undefined
        ? patch.coachProfile
        : clientsRouteCache.coachProfile,
    weekSummaries:
      patch.weekSummaries !== undefined
        ? patch.weekSummaries
        : clientsRouteCache.weekSummaries,
    activeClientId:
      patch.activeClientId !== undefined
        ? patch.activeClientId
        : clientsRouteCache.activeClientId,
  };
}

function maybeSyncWeekSummariesToClientsCache(coachId, nextSummaries) {
  const k = clientsRouteCacheKey(coachId);
  if (k && clientsRouteCache.storageKey === k) {
    clientsRouteCache.weekSummaries = { ...nextSummaries };
  }
}

const ClientsList = ({
  clients,
  coachProfile,
  activeClient,
  setActiveClient,
  onSelectClient,
  onAddClientClick,
  onCollapse,
  theme,
  weekSummaries = {},
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = React.useRef();

  const filteredClients = React.useMemo(() => {
    const allClients = coachProfile ? [coachProfile, ...clients] : clients;
    return allClients.filter(
      (client) =>
        client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [clients, coachProfile, searchQuery]);

  React.useEffect(() => {
    if (filteredClients.length > 0) {
      if (
        !activeClient ||
        !filteredClients.find((c) => c.id === activeClient.id)
      ) {
        setActiveClient(filteredClients[0]);
      }
    } else if (clients.length > 0 && searchQuery === "") {
      setActiveClient(clients[0]);
    } else {
      setActiveClient(null);
    }
  }, [filteredClients, activeClient]);

  // Arrow key navigation through client list
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't navigate when typing in search or other inputs
      const tagName = document.activeElement?.tagName?.toLowerCase();
      if (
        tagName === "input" ||
        tagName === "textarea" ||
        document.activeElement?.isContentEditable
      ) {
        return;
      }
      if (filteredClients.length === 0) return;

      const currentIndex = activeClient
        ? filteredClients.findIndex((c) => c.id === activeClient.id)
        : -1;
      const idx = currentIndex >= 0 ? currentIndex : 0;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        const next = (idx + 1) % filteredClients.length;
        setActiveClient(filteredClients[next]);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prev = idx <= 0 ? filteredClients.length - 1 : idx - 1;
        setActiveClient(filteredClients[prev]);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [filteredClients, activeClient, setActiveClient]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2 }}>
        {onCollapse && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <IconButton onClick={onCollapse} size="small">
              <ChevronLeftIcon />
            </IconButton>
          </Box>
        )}
        <Button onClick={onAddClientClick} fullWidth sx={{ mb: 2 }}>
          Add Client
        </Button>
        <TextField
          inputRef={searchInputRef}
          fullWidth
          size="small"
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
        />
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        {filteredClients.length ? (
          filteredClients.map((client, index) => {
            const isCoach = coachProfile && client.id === coachProfile.id;
            const summary = weekSummaries[client.id];
            const isActive = activeClient?.id === client.id;
            const noWorkoutsThisWeek =
              summary && !summary.loading && !summary.error && summary.total === 0;

            return (
              <Paper
                sx={{
                  mx: 2,
                  mb: 2,
                  p: 2,
                  backgroundColor: isActive
                    ? theme.palette.primary.main
                    : isCoach
                      ? "#f0f0f0"
                      : "white",
                  color: isActive ? "white" : "inherit",
                  cursor: "pointer",
                  overflow: "hidden",
                  border: isCoach
                    ? "2px solid #ff0000"
                    : noWorkoutsThisWeek && !isActive
                      ? `2px solid ${theme.palette.warning.main}`
                      : "none",
                  boxShadow:
                    noWorkoutsThisWeek && !isActive
                      ? `0 0 0 1px ${theme.palette.warning.light} inset`
                      : undefined,
                }}
                key={client.id}
                onClick={() => (onSelectClient || setActiveClient)(client)}
              >
                <Typography
                  color={isActive ? "white" : "inherit"}
                  sx={{
                    "& .email": {
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <b>
                    {client.name}
                    {isCoach && " (Me)"}
                  </b>
                  <br />
                  <span className="email">{client.email}</span>
                </Typography>
                {summary?.loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress
                      size={14}
                      sx={{
                        color: isActive ? "rgba(255,255,255,0.8)" : undefined,
                      }}
                    />
                    <Typography
                      variant="caption"
                      color={isActive ? "rgba(255,255,255,0.85)" : "text.secondary"}
                    >
                      Loading week…
                    </Typography>
                  </Box>
                ) : summary?.error ? (
                  <Typography
                    variant="caption"
                    color={isActive ? "#ffcdd2" : "error"}
                    sx={{ mt: 1, display: "block" }}
                  >
                    Week summary unavailable
                  </Typography>
                ) : summary ? (
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.5,
                    }}
                  >
                    {noWorkoutsThisWeek && (
                      <WarningAmberIcon
                        sx={{
                          fontSize: 18,
                          flexShrink: 0,
                          mt: "1px",
                          color: isActive ? "#ffecb3" : theme.palette.warning.dark,
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        color: isActive
                          ? noWorkoutsThisWeek
                            ? "#ffecb3"
                            : "rgba(255,255,255,0.9)"
                          : noWorkoutsThisWeek
                            ? theme.palette.error.main
                            : "text.secondary",
                        fontWeight: noWorkoutsThisWeek ? 600 : 400,
                        lineHeight: 1.35,
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {summary.weekLabel}
                      </Box>
                      <br />
                      {summary.total === 0 ? (
                        <>No workouts this week</>
                      ) : (
                        <>
                          {summary.total} workout{summary.total !== 1 ? "s" : ""}{" "}
                          · {summary.completed} completed
                        </>
                      )}
                    </Typography>
                  </Box>
                ) : null}
              </Paper>
            );
          })
        ) : searchQuery ? (
          <Paper sx={{ m: 2, p: 2 }}>
            <Typography sx={{ textAlign: "center", color: "text.secondary" }}>
              No clients found matching "{searchQuery}"
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{ m: 2, p: 2 }}>
            <Box>
              <Typography sx={{ textAlign: "center", color: "red" }}>
                <b>You have no clients!</b>
              </Typography>
              <Typography sx={{ mt: 2, mb: 3, textAlign: "center" }}>
                Click above to invite a client to join you on Trainer App.
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

const Clients = () => {
  const {
    getClients,
    getCoachSubscription,
    getUserProducts,
    getProducts,
    getWeekWorkoutSummariesForUsers,
  } = useApi();
  const {
    id: coachId,
    name: coachName,
    email: coachEmail,
    stripeAccountId,
  } = useUserInfo();
  const isPlatformUser = coachId === PLATFORM_OWNER_USER_ID;
  const [clients, setClients] = useState([]);
  const [coachProfile, setCoachProfile] = useState(null);
  const [openAddClient, setOpenAddClient] = useState(false);
  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeClient, setActiveClient] = useState(null);
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  const [clientHeaderActions, setClientHeaderActions] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [clientPurchasedProducts, setClientPurchasedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [weekSummaries, setWeekSummaries] = useState({});
  /** APIContext provides new function references every render; use a ref so callbacks stay stable. */
  const getWeekSummariesRef = useRef(getWeekWorkoutSummariesForUsers);
  getWeekSummariesRef.current = getWeekWorkoutSummariesForUsers;
  const fetchClientsRef = useRef(async () => {});
  const activeClientRef = useRef(null);
  const { getUserId } = useAuth();

  const clientCount = clients.length;

  useEffect(() => {
    activeClientRef.current = activeClient;
  }, [activeClient]);

  useEffect(() => {
    const uid = coachId || getUserId();
    if (!uid || !activeClient?.id) return;
    if (clientsRouteCache.storageKey !== clientsRouteCacheKey(uid)) return;
    clientsRouteCache.activeClientId = activeClient.id;
  }, [activeClient?.id, coachId, getUserId]);

  const loadWeekSummariesBatch = useCallback(async (clientRows, coachUserId) => {
    const { startUnix, endUnix, weekLabel } = getCurrentWeekUnixRange();
    const rows = Array.isArray(clientRows) ? clientRows : [];
    const idList = [
      ...(coachUserId ? [coachUserId] : []),
      ...rows.map((c) => c.id),
    ];
    const uniqueIds = [...new Set(idList.filter(Boolean))];
    if (uniqueIds.length === 0) {
      setWeekSummaries({});
      maybeSyncWeekSummariesToClientsCache(coachUserId, {});
      return;
    }

    setWeekSummaries((prev) => {
      const next = { ...prev };
      uniqueIds.forEach((id) => {
        next[id] = {
          ...(prev[id] || {}),
          loading: true,
          weekLabel,
          error: null,
        };
      });
      return next;
    });

    try {
      const res = await getWeekSummariesRef.current(
        uniqueIds,
        startUnix,
        endUnix
      );

      const map = {};
      for (const row of res.summaries || []) {
        if (row.error) {
          map[row.userId] = {
            total: 0,
            completed: 0,
            loading: false,
            weekLabel,
            error: new Error("Week summary failed"),
          };
        } else {
          map[row.userId] = {
            total: row.total ?? 0,
            completed: row.completed ?? 0,
            loading: false,
            weekLabel,
            error: null,
          };
        }
      }

      setWeekSummaries((prev) => {
        const next = { ...prev };
        uniqueIds.forEach((id) => {
          if (map[id]) next[id] = map[id];
          else {
            next[id] = {
              total: 0,
              completed: 0,
              loading: false,
              weekLabel,
              error: null,
            };
          }
        });
        maybeSyncWeekSummariesToClientsCache(coachUserId, next);
        return next;
      });
    } catch (e) {
      const wl = getCurrentWeekUnixRange().weekLabel;
      setWeekSummaries((prev) => {
        const next = { ...prev };
        uniqueIds.forEach((id) => {
          next[id] = {
            total: 0,
            completed: 0,
            loading: false,
            weekLabel: wl,
            error: e,
          };
        });
        maybeSyncWeekSummariesToClientsCache(coachUserId, next);
        return next;
      });
    }
  }, []);

  const patchWeekSummaryForClient = useCallback(
    async (userId) => {
      if (!userId) return;
      const { startUnix, endUnix, weekLabel } = getCurrentWeekUnixRange();
      setWeekSummaries((prev) => ({
        ...prev,
        [userId]: {
          ...(prev[userId] || {}),
          loading: true,
          weekLabel,
          error: null,
        },
      }));
      try {
        const res = await getWeekSummariesRef.current(
          [userId],
          startUnix,
          endUnix
        );
        const row = (res.summaries || []).find((r) => r.userId === userId);
        if (row?.error) throw new Error("Week summary failed");
        setWeekSummaries((prev) => {
          const next = {
            ...prev,
            [userId]: {
              total: row?.total ?? 0,
              completed: row?.completed ?? 0,
              loading: false,
              weekLabel,
              error: null,
            },
          };
          const uid = coachId || getUserId();
          maybeSyncWeekSummariesToClientsCache(uid, next);
          return next;
        });
      } catch (e) {
        setWeekSummaries((prev) => {
          const next = {
            ...prev,
            [userId]: {
              total: 0,
              completed: 0,
              loading: false,
              weekLabel,
              error: e,
            },
          };
          const uid = coachId || getUserId();
          maybeSyncWeekSummariesToClientsCache(uid, next);
          return next;
        });
      }
    },
    [coachId, getUserId]
  );

  const clearWorkoutQueryParams = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("workoutId");
        next.delete("exerciseIndex");
        next.delete("openComments");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const handleAddClientClick = async () => {
    if (isPlatformUser) {
      setOpenAddClient(true);
      return;
    }
    try {
      const sub = await getCoachSubscription();
      if (sub?.error) throw new Error(sub.error);

      // New coaches: active free trial — unlimited clients without subscribing
      if (sub?.freeTrialActive) {
        setOpenAddClient(true);
        return;
      }

      // Legacy coaches (no trial): one free client when no subscription; subscribe for more
      if (!sub?.hasSubscription) {
        if (clientCount >= 1) {
          setOpenSubscriptionModal(true);
          return;
        }
        setOpenAddClient(true);
        return;
      }

      const slots = sub?.slots ?? 0;
      if (clientCount >= slots) {
        setSubscription(sub);
        setOpenUpgradeModal(true);
        return;
      }

      setOpenAddClient(true);
    } catch (err) {
      console.error("Failed to check subscription:", err);
      setOpenAddClient(true);
    }
  };

  const handleSubscriptionSuccess = () => {
    setOpenSubscriptionModal(false);
    localStorage.removeItem("userInfo");
    window.location.reload();
  };

  const handleUpgradeSuccess = () => {
    setOpenUpgradeModal(false);
    setSubscription(null);
    setOpenAddClient(true);
  };

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const c = await getClients();
      const activeClients = Array.isArray(c)
        ? c.filter((client) => !client.deleted)
        : [];

      setClients(activeClients);

      const coach =
        coachId && coachName
          ? {
              id: coachId,
              name: coachName,
              email: coachEmail || "",
              isCoach: true,
            }
          : null;

      if (coach) {
        setCoachProfile(coach);
        setActiveClient((prev) => prev ?? coach);
      }

      // Week summaries: use this fetch snapshot + auth uid so we never wait on userProfile.id.
      const coachPk = coachId || getUserId() || null;
      const preferredActiveId =
        activeClientRef.current?.id ??
        coach?.id ??
        activeClients[0]?.id ??
        null;

      if (coachPk) {
        writeClientsRouteCache(coachPk, {
          clients: activeClients,
          coachProfile: coach,
          weekSummaries:
            clientsRouteCache.storageKey === clientsRouteCacheKey(coachPk)
              ? { ...clientsRouteCache.weekSummaries }
              : {},
          activeClientId: preferredActiveId,
        });
      }

      void loadWeekSummariesBatch(activeClients, coachPk);
    } catch (err) {
      console.error("fetchClients:", err);
    } finally {
      setLoading(false);
    }
  }, [
    coachId,
    coachName,
    coachEmail,
    getClients,
    getUserId,
    loadWeekSummariesBatch,
  ]);

  fetchClientsRef.current = fetchClients;

  useEffect(() => {
    const uid = coachId || getUserId();
    if (!uid) {
      fetchClientsRef.current();
      return;
    }
    const coachFromUser =
      coachId && coachName
        ? {
            id: coachId,
            name: coachName,
            email: coachEmail || "",
            isCoach: true,
          }
        : null;

    const snap = readClientsRouteCache(uid);
    if (snap && Array.isArray(snap.clients)) {
      setClients(snap.clients);
      setCoachProfile(coachFromUser ?? snap.coachProfile);
      setWeekSummaries(snap.weekSummaries || {});
      const profile = coachFromUser ?? snap.coachProfile;
      let nextActive = null;
      if (snap.activeClientId) {
        nextActive =
          snap.clients.find((c) => c.id === snap.activeClientId) ||
          (profile?.id === snap.activeClientId ? profile : null);
      }
      setActiveClient(nextActive || profile || snap.clients[0] || null);
      setLoading(false);
      if (coachFromUser) {
        writeClientsRouteCache(uid, { coachProfile: coachFromUser });
      }
      return;
    }
    fetchClientsRef.current();
  }, [coachId, coachName, coachEmail, getUserId]);

  // Deep link from notifications: ?clientId=&workoutId=&exerciseIndex=&openComments=
  useEffect(() => {
    const cid = searchParams.get("clientId");
    if (!cid || clients.length === 0) return;
    const match =
      clients.find((c) => c.id === cid) ||
      (coachProfile?.id === cid ? coachProfile : null);
    if (match) {
      setActiveClient(match);
    }
  }, [searchParams, clients, coachProfile]);

  const fetchClientProducts = async () => {
    if (!activeClient) {
      setClientPurchasedProducts([]);
      return;
    }
    if (activeClient.id === coachId) {
      setClientPurchasedProducts([]);
      return;
    }
    if (!stripeAccountId && !isPlatformUser) {
      setClientPurchasedProducts([]);
      return;
    }
    setProductsLoading(true);
    try {
      const accountId = isPlatformUser ? undefined : stripeAccountId;
      const [purchased, allProds] = await Promise.all([
        getUserProducts(activeClient.id, "purchased"),
        allProducts.length ? Promise.resolve(allProducts) : getProducts(accountId),
      ]);
      const list = Array.isArray(purchased)
        ? purchased
        : purchased?.items && Array.isArray(purchased.items)
          ? purchased.items
          : [];
      setClientPurchasedProducts(list);
      if (!allProducts.length) {
        setAllProducts(allProds || []);
      }
    } catch (err) {
      console.error("Failed to load client products:", err);
      setClientPurchasedProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    fetchClientProducts();
  }, [activeClient?.id, coachId, stripeAccountId, isPlatformUser]);

  const productInfoMap = useMemo(() => {
    const map = {};
    for (const product of allProducts) {
      for (const price of product.prices || []) {
        const key = price.priceId ?? price.price_id;
        if (key) map[key] = { name: product.name, price };
      }
    }
    return map;
  }, [allProducts]);

  // Combine user-products with product names from catalog (same name as pills)
  const purchasedProductsWithNames = useMemo(() => {
    const list = Array.isArray(clientPurchasedProducts)
      ? clientPurchasedProducts
      : [];
    return list.map((p) => {
      const priceId = p.priceId ?? p.price_id ?? "";
      const info = productInfoMap[priceId];
      const productName =
        p.productName ?? info?.name ?? "Product";
      return { ...p, priceId: priceId || p.priceId, productName };
    });
  }, [clientPurchasedProducts, productInfoMap]);

  const combinedProducts = useMemo(() => {
    const list = Array.isArray(clientPurchasedProducts)
      ? clientPurchasedProducts
      : [];
    const byKey = {};
    list.forEach((p, index) => {
      // Normalize key so same product+price always consolidates (API may return priceId or price_id)
      const priceId = p.priceId ?? p.price_id ?? "";
      const productId = p.productId ?? p.product_id ?? "";
      const key = [priceId, productId].filter(Boolean).join("_") || `__noId_${index}`;
      if (!byKey[key]) {
        byKey[key] = { ...p, priceId: priceId || p.priceId, productId: productId || p.productId };
      } else {
        const existing = byKey[key];
        const isRecurring = !!existing.recurring || !!p.recurring;
        if (!isRecurring) {
          existing.quantityLeft = (existing.quantityLeft ?? 0) + (p.quantityLeft ?? 0);
        }
        // Keep first defined display fields
        if (!existing.productName && p.productName) existing.productName = p.productName;
        if (existing.recurring == null && p.recurring != null) existing.recurring = p.recurring;
        if (existing.status == null && p.status != null) existing.status = p.status;
        if (existing.cancel_at_period_end == null && p.cancel_at_period_end != null)
          existing.cancel_at_period_end = p.cancel_at_period_end;
      }
    });
    return Object.values(byKey);
  }, [clientPurchasedProducts]);

  // If any purchased product has quantityLeft > 0, show only those; if all are 0, show all
  const displayProducts = useMemo(() => {
    const list = combinedProducts;
    const hasAnyWithQuantity = list.some((p) => (p.quantityLeft ?? 0) > 0);
    if (hasAnyWithQuantity) {
      return list.filter((p) => (p.quantityLeft ?? 0) > 0);
    }
    return list;
  }, [combinedProducts]);

  if (loading) {
    return (
      <Container sx={{ mt: 10 }}>
        <Loading />
      </Container>
    );
  }

  const SIDEBAR_WIDTH = 320;

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: 0,
        height: "calc(100vh - 72px)",
        mt: 1,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: isMobile ? 0 : SIDEBAR_WIDTH,
          overflow: "hidden",
          display: isMobile ? (sidebarOpen ? "flex" : "none") : "flex",
          flexDirection: "column",
          minHeight: 0,
          borderRight: isMobile ? 0 : 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
          ...(isMobile && {
            position: "fixed",
            top: 72,
            left: 0,
            bottom: 0,
            width: SIDEBAR_WIDTH,
            zIndex: 1200,
            boxShadow: 3,
          }),
        }}
      >
        <ClientsList
          clients={clients}
          coachProfile={coachProfile}
          activeClient={activeClient}
          setActiveClient={setActiveClient}
          onSelectClient={
            isMobile
              ? (client) => {
                  setActiveClient(client);
                  setSidebarOpen(false);
                }
              : undefined
          }
          onAddClientClick={handleAddClientClick}
          onCollapse={isMobile ? () => setSidebarOpen(false) : undefined}
          theme={theme}
          weekSummaries={weekSummaries}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "auto",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
              {isMobile && (
                <IconButton onClick={() => setSidebarOpen(true)} size="small" sx={{ flexShrink: 0 }}>
                  <MenuIcon />
                </IconButton>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                {activeClient && (
                  <Typography variant="h6" noWrap>
                    {activeClient.name}
                  </Typography>
                )}
                {productsLoading ? (
                  <CircularProgress size={16} sx={{ mt: 0.5 }} />
                ) : displayProducts.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {displayProducts.map((purchasedProduct, index) => {
                      const info = productInfoMap[purchasedProduct.priceId];
                      const name =
                        purchasedProduct.productName ?? info?.name ?? "Product";
                      const isRecurring = !!(
                        purchasedProduct.recurring ?? info?.price?.recurring
                      );
                      const isRed = isRecurring
                        ? purchasedProduct.status === "cancelled" ||
                          !!purchasedProduct.cancel_at_period_end
                        : (purchasedProduct.quantityLeft ?? 0) === 0;
                      return (
                        <Chip
                          key={purchasedProduct.priceId ?? index}
                          label={
                            isRecurring
                              ? name
                              : `${name} (${purchasedProduct.quantityLeft ?? 0})`
                          }
                          size="small"
                          variant="outlined"
                          onClick={() => setProductsDialogOpen(true)}
                          sx={{
                            cursor: "pointer",
                            color: isRed ? "error.main" : "text.secondary",
                            borderColor: isRed ? "error.main" : "divider",
                            "&:hover": { opacity: 0.75 },
                          }}
                        />
                      );
                    })}
                  </Box>
                ) : null}
              </Box>
            </Box>
            <Box sx={{ flexShrink: 0 }}>{clientHeaderActions}</Box>
          </Box>
          {activeClient && (
            <ClientWorkoutCalendar
              clientId={activeClient.id}
              onWorkoutsChanged={patchWeekSummaryForClient}
              notificationWorkoutParams={
                searchParams.get("workoutId")
                  ? {
                      workoutId: searchParams.get("workoutId"),
                      exerciseIndex: searchParams.get("exerciseIndex"),
                      openComments: searchParams.get("openComments") === "1",
                      queryClientId: searchParams.get("clientId"),
                    }
                  : null
              }
              onNotificationWorkoutConsumed={clearWorkoutQueryParams}
              onClientDeleted={() => {
                setActiveClient(null);
                fetchClients();
              }}
              onClientUpdated={(updated) => {
                if (updated?.name) {
                  setActiveClient((prev) => (prev ? { ...prev, name: updated.name } : null));
                }
                fetchClients();
              }}
              setHeaderActions={setClientHeaderActions}
              onProductsSaved={fetchClientProducts}
            />
          )}
        </Box>
      </Box>

      {openAddClient && (
        <AddClient
          onClose={() => setOpenAddClient(false)}
          onAddClient={() => {
            fetchClients();
          }}
        />
      )}
      {openSubscriptionModal && (
        <Elements
          stripe={stripePromise}
          options={{
            appearance,
            fonts: [
              {
                family: "Roboto Mono, monospace",
                cssSrc:
                  "https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@100;400;500;700",
              },
            ],
          }}
        >
          <CoachSubscriptionModal
            open={openSubscriptionModal}
            onClose={() => setOpenSubscriptionModal(false)}
            onSuccess={handleSubscriptionSuccess}
          />
        </Elements>
      )}
      {openUpgradeModal && subscription && (
        <CoachUpgradeModal
          open={openUpgradeModal}
          onClose={() => {
            setOpenUpgradeModal(false);
            setSubscription(null);
          }}
          currentSlots={subscription.slots}
          currentPaymentsPro={subscription.paymentsPro}
          clientCount={clientCount}
          subscriptionId={subscription.subscriptionId}
          onSuccess={handleUpgradeSuccess}
        />
      )}
      <ManageProductsDialog
        open={productsDialogOpen}
        onClose={() => setProductsDialogOpen(false)}
        onSaved={fetchClientProducts}
        client={activeClient}
        initialProducts={allProducts}
        initialPurchasedProducts={purchasedProductsWithNames}
      />
    </Box>
  );
};

export default Clients;
